import { ExpandCircleDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import moment from 'moment-timezone';
import displayConfig from '../../config/displayConfig';
import appConfig from '../../config/appConfig';
import BackButton from '../BackButton';
import { RoutingPaths } from '../../constants';
import { PSContext } from '../../Contexts';
import IDKeys from '../../constants/idKeys';

/**
 * A dropdown selection menu that allows users to change the PS section they are viewing.
 * @param {object} props Props accepted by the component.
 * @param {string} props.sectionID The ID of the PS section the user is currently viewing.
 * @returns {import('react').FunctionComponent} React element that contains the selection menu.
 */
function SectionSelect({ sectionID }) {
  const [translate, i18n] = useTranslation();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const renderValue = (value) => (
    <Stack alignItems="flex-start" gap={1}>
      <Typography
        id={`${IDKeys.SECTION_SELECT}TitleText`}
        variant="h_md"
        sx={{ textWrap: 'wrap' }}
      >
        {translate(`sections.${value}.title`)}
      </Typography>
      <Typography
        id={`${IDKeys.SECTION_SELECT}SubtitleText`}
        variant="body_xs"
        color="text.secondary"
        sx={{ textWrap: 'wrap' }}
      >
        {translate(`sections.${value}.subtitle`)}
      </Typography>
    </Stack>
  );

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (section) => {
    // If user has changed the section, then navigate to the new page
    if (sectionID !== section) {
      navigate(`../${section}?lang=${i18n.language}`, { replace: true });
    }
    handleClose();
  };
  // Get supported sections from the display config
  const sectionConfigs = displayConfig[appConfig.spec].sections;
  const { shl } = useContext(PSContext);

  return (
    <Box role="navigation" id="section-select">
      <BackButton
        testID={`${IDKeys.SECTION_SELECT}BackButton`}
        homeURL={`/${RoutingPaths.VIEWER}/${shl}?lang=${i18n.language}`}
      />
      <Button
        id={IDKeys.SECTION_SELECT}
        aria-label={translate('sectionSelect.buttonLabel')}
        onClick={handleClick}
        variant="outlined"
        aria-haspopup="true"
        disableFocusRipple
        endIcon={
          <ExpandCircleDown
            sx={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        }
        sx={{
          justifyContent: 'space-between',
          width: '100%',
          padding: '10px 14px',
          color: 'inherit',
          backgroundColor: 'inherit',
          borderColor: 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderColor: 'transparent',
            boxShadow: 'none',
          },
          '&:focus': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {renderValue(sectionID)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              width: anchorEl ? anchorEl.clientWidth : undefined,
            },
          },
        }}
      >
        <Box role="menu" aria-label={translate('sectionSelect.menuLabel')}>
          {sectionConfigs.map((sectionConfig, i) => {
            const itemId = `${moment().millisecond()}_${i}`;
            return (
              <MenuItem
                key={itemId}
                id={`${IDKeys.SECTION_SELECT}MenuItem-${i}`}
                selected={sectionID === sectionConfig.id}
                onClick={() => handleSelect(sectionConfig.id)}
              >
                {translate(sectionConfig.titleLabel)}
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    </Box>
  );
}

export default SectionSelect;
